<template>
  <div class="edit-work-info-wrapper">
    <div class="edit-work-info-container">
      <div class="route-name">{{ $t(`message.sideMenu.${this.$route.name}`) }}</div>
      <div class="back-block" @click="$router.push({ name: 'change-work-info' })">
        <img
          src="../../../../public/custom/images/back-arrow.svg"
          class="back-img"
          alt="Back arrow"
        />
        <span class="back-btn-text">{{ $t('message.loanPage.back') }}</span>
      </div>
      <div v-if="isReady" class="info-section">
        <div class="work-info-form">
          <div class="form-field">
            <div class="form-label">{{ $t('message.editWorkInfoPage.fields.employmentType') }}</div>
            <div v-if="unresolved.employment_type" class="form-current">
              {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ unresolved.employment_type }}
            </div>
            <base-select
              :default-value="form.employment_type"
              :dictionary-path="'message.signUp.jobBlock.typeOfEmployment.list.'"
              :options-list="employmentTypeOptions"
              :name="'employment_type'"
              v-model="form.employment_type"
            />
          </div>
          <div class="form-field">
            <div class="form-label">{{ $t('message.editWorkInfoPage.fields.scopeOfEmployment') }}</div>
            <div v-if="unresolved.scope_of_employment_type" class="form-current">
              {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ unresolved.scope_of_employment_type }}
            </div>
            <base-select
              :default-value="form.scope_of_employment_type"
              :dictionary-path="'message.signUp.jobBlock.fieldOfEmployment.list.'"
              :options-list="CONSTANTS.SCOPE_OF_EMPLOYMENT_TYPE_LIST"
              :name="'scope_of_employment_type'"
              :disabled="isEmploymentWorkFieldsDisabled"
              v-model="form.scope_of_employment_type"
            />
          </div>
          <div class="form-field">
            <div class="form-label">{{ $t('message.editWorkInfoPage.fields.jobPosition') }}</div>
            <div v-if="unresolved.job_position_type" class="form-current">
              {{ $t('message.editWorkInfoPage.currentPrefix') }} {{ unresolved.job_position_type }}
            </div>
            <base-select
              :default-value="form.job_position_type"
              :dictionary-path="'message.signUp.jobBlock.post.list.'"
              :options-list="CONSTANTS.POST_TYPE_LIST"
              :name="'job_position_type'"
              :disabled="isEmploymentWorkFieldsDisabled"
              v-model="form.job_position_type"
            />
          </div>
          <div v-if="isOtherJobPositionType" class="form-field">
            <div class="form-label">{{ $t('message.editWorkInfoPage.fields.jobPositionName') }}</div>
            <base-text-input
              :default-value="form.job_position_name"
              :name="'job_position_name'"
              :is-has-error-block="false"
              :disabled="isEmploymentWorkFieldsDisabled"
              v-model="form.job_position_name"
            />
          </div>
          <div class="form-field">
            <div class="form-label">{{ $t('message.editWorkInfoPage.fields.organizationName') }}</div>
            <base-text-input
              :default-value="form.organization_name"
              :name="'organization_name'"
              :is-has-error-block="false"
              :disabled="isEmploymentWorkFieldsDisabled"
              v-model="form.organization_name"
            />
          </div>
          <div class="form-field">
            <div class="form-label">{{ $t('message.editWorkInfoPage.fields.workExperience') }}</div>
            <base-text-input
              :default-value="form.workExperience"
              :name="'workExperience'"
              :is-has-error-block="false"
              :mask-pattern="'####'"
              :disabled="isEmploymentWorkFieldsDisabled"
              v-model="form.workExperience"
            />
          </div>
        </div>
        <div class="form-actions">
          <div class="form-btn-container">
            <base-button
              :text="$t('message.editWorkInfoPage.btnSave')"
              :disabled="isSaveDisabled || isSaveBtnLoading"
              :is-loading="isSaveBtnLoading"
              @click="saveWorkInfo()"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseButton from '../../../components/base/BaseButton.vue';
import BaseSelect from '../../../components/base/BaseSelect.vue';
import BaseTextInput from '../../../components/base/BaseTextInput.vue';
import CONSTANTS from '../../../constants/constants';
import {
  buildWorkInfoEditForm,
  getDefaultEmploymentWorkFields,
  getDisabledEmploymentWorkFields,
  getWorkInfoEmploymentTypeOptions,
  isCustomJobPositionType,
  isEmploymentTypeWithDisabledWorkFields
} from '../../../helpers/workInfo';

export default {
  name: 'EditWorkInfoLender',
  inject: ['$httpService'],
  components: {
    BaseButton,
    BaseSelect,
    BaseTextInput
  },
  data() {
    return {
      CONSTANTS,
      employmentTypeOptions: getWorkInfoEmploymentTypeOptions(),
      isReady: false,
      isSaveBtnLoading: false,
      form: {
        employment_type: '',
        scope_of_employment_type: '',
        organization_name: '',
        job_position_type: '',
        job_position_name: '',
        workExperience: ''
      },
      unresolved: {
        employment_type: null,
        scope_of_employment_type: null,
        job_position_type: null
      }
    };
  },
  async created() {
    await this.$store.dispatch('getProfile');

    if (this.profile?.role !== 'lender') {
      this.$router.replace({ name: 'change-work-info' });
      return;
    }

    this.setFormData();
  },
  computed: {
    ...mapGetters(['profile']),
    isOtherJobPositionType() {
      return isCustomJobPositionType(this.form.job_position_type);
    },
    isEmploymentWorkFieldsDisabled() {
      return isEmploymentTypeWithDisabledWorkFields(this.form.employment_type);
    },
    isSaveDisabled() {
      if (this.isEmploymentWorkFieldsDisabled) {
        return !this.form.employment_type;
      }

      return !this.form.employment_type
        || !this.form.scope_of_employment_type
        || !this.form.job_position_type
        || (this.isOtherJobPositionType && !String(this.form.job_position_name || '').trim())
        || !String(this.form.organization_name || '').trim();
    }
  },
  watch: {
    'form.employment_type'(value, previousValue) {
      if (!this.isReady) {
        return;
      }

      if (isEmploymentTypeWithDisabledWorkFields(value)) {
        this.setDisabledEmploymentWorkFields();
        return;
      }

      if (isEmploymentTypeWithDisabledWorkFields(previousValue)) {
        this.setDefaultEmploymentWorkFields();
      }
    }
  },
  methods: {
    setFormData() {
      const formState = buildWorkInfoEditForm(this.profile?.work_info);

      this.form = {
        employment_type: formState.employment_type,
        scope_of_employment_type: formState.scope_of_employment_type,
        organization_name: formState.organization_name,
        job_position_type: formState.job_position_type,
        job_position_name: formState.job_position_name,
        workExperience: formState.workExperience
      };
      this.unresolved = formState.unresolved;

      if (isEmploymentTypeWithDisabledWorkFields(this.form.employment_type)) {
        this.setDisabledEmploymentWorkFields();
      }

      this.isReady = true;
    },
    setDisabledEmploymentWorkFields() {
      const disabledFields = getDisabledEmploymentWorkFields();

      this.form.scope_of_employment_type = disabledFields.scope_of_employment_type;
      this.form.organization_name = disabledFields.organization_name;
      this.form.job_position_type = disabledFields.job_position_type;
      this.form.job_position_name = disabledFields.job_position_name;
      this.form.workExperience = disabledFields.workExperience;
    },
    setDefaultEmploymentWorkFields() {
      const defaultFields = getDefaultEmploymentWorkFields();

      this.form.scope_of_employment_type = defaultFields.scope_of_employment_type;
      this.form.organization_name = defaultFields.organization_name;
      this.form.job_position_type = defaultFields.job_position_type;
      this.form.job_position_name = defaultFields.job_position_name;
    },
    async saveWorkInfo() {
      if (this.isSaveDisabled || this.isSaveBtnLoading) {
        return;
      }

      this.isSaveBtnLoading = true;

      try {
        const workInfo = {
          employment_type: this.form.employment_type,
          scope_of_employment_type: this.form.scope_of_employment_type,
          organization_name: this.form.organization_name,
          job_position_type: this.form.job_position_type,
          workExperience: this.form.workExperience
        };

        if (isCustomJobPositionType(this.form.job_position_type)) {
          workInfo.job_position_name = this.form.job_position_name;
        }

        const response = await this.$httpService.auth.editLenderWorkInfo(workInfo);

        if (!response) {
          throw new Error('Edit lender work info failed');
        }

        this.$router.push({ name: 'change-work-info' });
      } catch (error) {
        this.isSaveBtnLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.edit-work-info-wrapper {
  @include page-wrapper-with-header-and-footer;

  padding-top: 20px;
  padding-bottom: 40px;
  box-sizing: border-box;
}

.edit-work-info-container {
  width: 95%;
  min-height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
}

.back-block {
  width: fit-content;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.back-img {
  margin-right: 4px;
  height: 18px;
  width: 18px;
}

.back-btn-text {
  font-weight: 400;
  font-size: 16px;
  line-height: 135%;
  color: #878787;
}

.route-name {
  @include route-name;
  margin-bottom: 15px;
}

.info-section {
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  border-radius: 12px;
  background-color: #FFFFFF;
  border: 1px solid #ECEDEE;
  box-shadow: 0 2px 8px rgba(1, 39, 59, 0.06);
}

.work-info-form {
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-field {
  width: 100%;
}

.form-label {
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: #7A7A7A;
}

.form-field ::v-deep .input-block,
.form-field ::v-deep .select-block {
  height: 40px;
  min-height: 40px;
  border-radius: 6px;
}

.form-field ::v-deep .input {
  height: 38px;
  min-height: 38px;
  padding: 8px;
  font-size: 14px;
  line-height: 24px;
  color: #01273B;
}

.form-field ::v-deep .select-block .input {
  height: 100%;
  min-height: 100%;
}

.form-field ::v-deep .select-block.disabled {
  background-color: rgba(239, 239, 239, 0.3);
  cursor: default;
}

.form-field ::v-deep .input:disabled {
  background-color: rgba(239, 239, 239, 0.3);
  cursor: default;
}

.form-field ::v-deep .error-block {
  height: 16px;
  margin-top: 2px;
  font-size: 12px;
  line-height: 16px;
}

.form-current {
  margin-bottom: 8px;
  padding: 10px 12px;
  box-sizing: border-box;
  border: 1px solid #ECEDEE;
  border-radius: 8px;
  background: #F8FAF9;
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: #333333;
}

.form-actions {
  width: 100%;
  max-width: 520px;
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.form-btn-container {
  width: 200px;
  height: 40px;
}

.form-btn-container ::v-deep .button {
  min-height: 40px;
  max-height: 40px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 24px;
}

@media screen and (max-width: 1155px) {
  .edit-work-info-wrapper {
    padding-top: 40px;
  }

  .edit-work-info-container {
    @include base-page-container;

    align-items: flex-start;
    padding-top: 0;
  }
}

@media screen and (max-width: 600px) {
  .form-btn-container {
    width: 100%;
  }
}
</style>
